import React from 'react';
import clsx from 'clsx';

/**
 * Form-field presentation, shared by every discography editing surface.
 *
 * There are three places a user edits the same recording fields — the recording
 * edit form (one recording, inline), the mass-edit quick-edit modals (one
 * recording at a time, from the grid) and the bulk-edit builder (one plan, many
 * recordings) — plus the leaf modals (contribution detail, artist editor,
 * change release) that all three open. They had drifted into two different
 * visual languages: the mass/bulk surfaces used the portal's design tokens
 * (`formInputBackground`, `textDove`, `textSeal`), while the edit form and the
 * leaf modals used ad-hoc `gray-700` / `gray-600`. The leaf modals are the
 * clearest symptom: opening "Contribution" from bulk edit changed the input
 * styling halfway through the flow.
 *
 * The token language wins here because it is not just the mass-edit style — it
 * is what `shared_components/components/WForms/WInput` renders, i.e. what every
 * other form in the portal already looks like. So this module is that style,
 * written once:
 *
 *   - {@link FIELD_INPUT_CLASS} / {@link FIELD_TEXTAREA_CLASS} /
 *     {@link FIELD_SELECT_CLASS} — the controls themselves.
 *   - {@link fieldControlClass} — the same, plus the error / locked states, so
 *     an invalid field looks identical wherever it is shown.
 *   - {@link FIELD_LABEL_CLASS} and friends — label, "Optional" tag, hint,
 *     explanatory copy, error copy.
 *   - {@link DROPDOWN_PANEL_CLASS} and friends — the search-results panel used
 *     by the role and artist pickers.
 *   - {@link SegmentedToggle} — the Featured / Other Featured style two-option
 *     switch, previously copy-pasted into three files with three different
 *     paddings.
 *
 * Chips/pills are deliberately not here: they already come from the shared
 * `manualChangeChip` module and were never divergent.
 */

/* -------------------------------------------------------------------------- */
/* Controls                                                                   */
/* -------------------------------------------------------------------------- */

/** Text input. `h-11` fixes the control height so inputs and selects line up. */
export const FIELD_INPUT_CLASS = clsx(
  'tw-w-full',
  'tw-h-11',
  'tw-p-3',
  'tw-rounded-lg',
  'tw-border-2',
  'tw-border-formInputBackground',
  'tw-bg-formInputBackground',
  'tw-text-white',
  'tw-text-sm',
  'tw-placeholder-textSeal',
  'focus:tw-border-white',
  'focus:tw-outline-none',
  'disabled:tw-cursor-not-allowed',
  'disabled:tw-opacity-60'
);

/** Textarea — same skin, but it has to be free to grow. */
export const FIELD_TEXTAREA_CLASS = clsx(
  FIELD_INPUT_CLASS,
  'tw-h-auto',
  'tw-resize-y'
);

/** Select. `wmc-select` (global.css) replaces the native arrow and sets the
 *  right padding that leaves room for it, so the left padding is set here to
 *  match rather than using the input's symmetric `p-3`. */
export const FIELD_SELECT_CLASS = clsx(
  FIELD_INPUT_CLASS,
  'wmc-select',
  'tw-pl-3'
);

/** White text once a real option is chosen; placeholder colour while the empty
 *  default option is showing, so a select reads like an unfilled input. */
export const fieldSelectTextClass = (hasValue: boolean): string =>
  hasValue ? 'tw-text-white' : 'tw-text-textSeal';

/**
 * A value the user can read but not edit — a looked-up release's title, a
 * system-set field. Deliberately not the same skin as a disabled input: a
 * dimmed-but-input-shaped control reads as "editable, but not right now",
 * whereas these never become editable. Sunk one shade instead.
 */
export const FIELD_READONLY_CLASS = clsx(
  'tw-w-full',
  'tw-min-h-[2.75rem]',
  'tw-p-3',
  'tw-rounded-lg',
  'tw-border-2 tw-border-formInputBackground',
  'tw-bg-surfaceBlack90',
  'tw-text-white tw-text-sm'
);

/** Error border, applied on top of a control class. */
export const FIELD_ERROR_BORDER_CLASS =
  'tw-border-westburyRed focus:tw-border-westburyRed';

/** Dimming for a control that is present but not editable (write-once gates). */
export const FIELD_LOCKED_CLASS = 'tw-opacity-60 tw-cursor-not-allowed';

/**
 * A control's full class list, including its state.
 *
 * Preferred over hand-concatenating {@link FIELD_INPUT_CLASS} with an error
 * class: the error and locked treatments are what most often drifted between
 * surfaces (one used `red-500`, another `westburyRed`, a third nothing at all).
 */
export function fieldControlClass(
  options: {
    /** 'input' (default), 'textarea' or 'select'. */
    kind?: 'input' | 'textarea' | 'select';
    /** Draw the invalid-value border. */
    error?: boolean;
    /** Dim as not-editable. Pair with the control's own `disabled`. */
    locked?: boolean;
    /** For selects: whether a real option is chosen (drives the text colour). */
    hasValue?: boolean;
    /** Anything else to append. */
    className?: string;
  } = {}
): string {
  const { kind = 'input', error, locked, hasValue, className } = options;
  const base =
    kind === 'textarea'
      ? FIELD_TEXTAREA_CLASS
      : kind === 'select'
      ? FIELD_SELECT_CLASS
      : FIELD_INPUT_CLASS;
  return clsx(
    base,
    kind === 'select' && fieldSelectTextClass(!!hasValue),
    error && FIELD_ERROR_BORDER_CLASS,
    locked && FIELD_LOCKED_CLASS,
    className
  );
}

/* -------------------------------------------------------------------------- */
/* Copy around a field                                                        */
/* -------------------------------------------------------------------------- */

/** The field's name. */
export const FIELD_LABEL_CLASS =
  'tw-block tw-text-sm tw-font-medium tw-text-white';

/** The "Optional" tag that sits opposite a label. */
export const FIELD_OPTIONAL_CLASS = 'tw-text-xs tw-leading-4 tw-text-textSeal';

/** A hint under a control (format reminders, empty-state notes). */
export const FIELD_HINT_CLASS = 'tw-text-xs tw-text-textSeal';

/** Explanatory copy at the top of a modal — what this edit does, and its rules. */
export const FIELD_HELP_CLASS = 'tw-text-sm tw-text-textDove';

/** A validation message. */
export const FIELD_ERROR_CLASS = 'tw-text-xs tw-text-westburyRed';

/** The row that holds a label and, opposite it, an "Optional" tag or an action. */
export const FIELD_LABEL_ROW_CLASS =
  'tw-flex tw-items-center tw-justify-between tw-mb-2';

/** A modal's Cancel / confirm row. */
export const MODAL_ACTIONS_CLASS = 'tw-flex tw-justify-end tw-gap-2 tw-pt-2';

/* -------------------------------------------------------------------------- */
/* Search-results dropdown                                                    */
/* -------------------------------------------------------------------------- */

/** The floating panel under a search input. */
export const DROPDOWN_PANEL_CLASS = clsx(
  'tw-absolute tw-z-[60] tw-left-0 tw-right-0 tw-top-full tw-mt-1',
  'tw-max-h-48 tw-overflow-y-auto',
  'tw-rounded-lg tw-border-2 tw-border-formInputBackground',
  'tw-bg-surfaceBlack80 tw-py-1 tw-shadow-lg'
);

/** One selectable result. */
export const DROPDOWN_OPTION_CLASS =
  'tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-surfaceBlack90 tw-transition-colors';

/** "No matches" / "Searching…" / "Start typing…" placeholder inside the panel. */
export const DROPDOWN_EMPTY_CLASS =
  'tw-px-3 tw-py-2 tw-text-textDove tw-text-xs';

/** The dimmer id shown after a result's name. */
export const DROPDOWN_META_CLASS = 'tw-text-textSeal tw-text-xs tw-ml-2';

/**
 * Type-to-filter picker over a fixed list of options — contribution roles, and
 * anything else with a known vocabulary.
 *
 * Selection happens on `onMouseDown` (before the input's blur closes the panel)
 * and the query is cleared afterwards, so picking twice in a row works without
 * the previous choice still filtering the list.
 */
export const SearchSelect: React.FC<{
  options: string[];
  /** Already-chosen values, filtered out of the results. */
  exclude?: string[];
  onSelect: (value: string) => void;
  placeholder?: string;
  /** Rendering for an option's label (e.g. capitalisation). */
  renderOption?: (value: string) => React.ReactNode;
}> = ({ options, exclude = [], onSelect, placeholder, renderOption }) => {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => (!q || o.toLowerCase().includes(q)) && !exclude.includes(o)
    );
  }, [options, exclude, query]);
  return (
    <div className="tw-relative">
      <input
        className={FIELD_INPUT_CLASS}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={placeholder ?? 'Search or select…'}
      />
      {open && (
        <div className={DROPDOWN_PANEL_CLASS}>
          {filtered.length === 0 ? (
            <div className={DROPDOWN_EMPTY_CLASS}>No matches</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o}
                type="button"
                className={DROPDOWN_OPTION_CLASS}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(o);
                  setQuery('');
                  setOpen(false);
                }}
              >
                {renderOption ? renderOption(o) : o}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Segmented toggle                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Two-option switch (lime fill on the selected side), used for Featured /
 * Other Featured and for the add-alias/band "Is permanent member?" choice.
 *
 * One component rather than the three near-identical local copies that existed
 * before — they had drifted on text size and on whether the unselected side
 * highlighted on hover.
 */
export const SegmentedToggle: React.FC<{
  leftLabel: string;
  rightLabel: string;
  isLeftSelected: boolean;
  onSelectLeft: () => void;
  onSelectRight: () => void;
  disabled?: boolean;
}> = ({
  leftLabel,
  rightLabel,
  isLeftSelected,
  onSelectLeft,
  onSelectRight,
  disabled = false,
}) => {
  const side = (selected: boolean, rounding: string) =>
    clsx(
      'tw-px-4 tw-py-1.5 tw-text-sm tw-transition-colors',
      rounding,
      selected
        ? 'tw-bg-surfaceLime tw-text-black'
        : 'tw-bg-formInputBackground tw-text-white',
      !selected && !disabled && 'hover:tw-bg-surfaceLime hover:tw-text-black',
      disabled && 'tw-opacity-60 tw-cursor-not-allowed'
    );
  return (
    <div className="tw-flex tw-items-center">
      <button
        type="button"
        disabled={disabled}
        onClick={onSelectLeft}
        className={side(isLeftSelected, 'tw-rounded-l-md')}
      >
        {leftLabel}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={onSelectRight}
        className={side(!isLeftSelected, 'tw-rounded-r-md')}
      >
        {rightLabel}
      </button>
    </div>
  );
};
